import { useState } from "react";
import { motion } from "motion/react";
import { ArrowUpRight, Send, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

export default function CtaFooter({
  headline = "Ready to grow your business with AI?",
  subtext = "Book a free strategy call with EsyAI. Whether you need a professional website, business automation, a custom app, or AI agents — we'll show you exactly what's possible. No commitment. No pressure.",
  primaryButtonText = "Book a Call",
  primaryButtonLink = "#contact-form",
  secondaryButtonText = "View Pricing",
  secondaryButtonLink = "#features",
  showSecondaryButton = true
}) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");
    try {
      // For UI demonstration purposes, we will mock the backend call.
      // Uncomment the `axios.post` line below when your backend server is running.
      // await axios.post(`${API}/contact`, form);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setStatus("success");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch (error) {
      console.error("Form Submission Error:", error);
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-black" data-testid="cta-footer">


      {/* Content */}
      <div className="relative z-10 px-4 sm:px-8 lg:px-16 py-16">
        <div className="max-w-6xl mx-auto">
          {/* CTA */}
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.6 }}
              className="text-5xl md:text-6xl lg:text-7xl font-heading italic text-white leading-[0.85] tracking-tight"
              data-testid="cta-heading"
            >
              {headline}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-white/60 font-body font-light text-sm md:text-base max-w-lg mx-auto"
              data-testid="cta-subtext"
            >
              {subtext}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center justify-center gap-4 mt-8"
            >
              <a
                href={primaryButtonLink}
                className="liquid-glass-strong rounded-full px-6 py-3 text-white text-sm font-medium font-body flex items-center gap-2 hover:bg-white/10 transition-colors"
                data-testid="cta-book-btn"
              >
                {primaryButtonText} <ArrowUpRight className="w-4 h-4" />
              </a>
              {showSecondaryButton && (
                <a
                  href={secondaryButtonLink}
                  className="bg-white text-black rounded-full px-6 py-3 text-sm font-medium font-body hover:bg-white/90 transition-colors"
                  data-testid="cta-pricing-btn"
                >
                  {secondaryButtonText}
                </a>
              )}
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div
            id="contact-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.6 }}
            className="max-w-md mx-auto"
          >
            <div className="liquid-glass rounded-2xl p-8">
              <h3
                className="text-2xl font-heading italic text-white mb-6 text-center"
                data-testid="contact-form-title"
              >
                Get in touch
              </h3>

              {status === "success" ? (
                <div className="flex flex-col items-center gap-3 py-8" data-testid="contact-success">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                  <p className="text-white font-body text-sm">
                    Message sent! We'll be in touch soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="contact-form">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                    className="bg-transparent border border-white/20 rounded-xl px-4 py-3 text-white text-sm font-body font-light placeholder:text-white/40 focus:border-white/50 focus:outline-none transition-colors"
                    required
                    data-testid="contact-name-input"
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    className="bg-transparent border border-white/20 rounded-xl px-4 py-3 text-white text-sm font-body font-light placeholder:text-white/40 focus:border-white/50 focus:outline-none transition-colors"
                    required
                    data-testid="contact-email-input"
                  />
                  <textarea
                    placeholder="Tell us about your project"
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    rows={4}
                    className="bg-transparent border border-white/20 rounded-xl px-4 py-3 text-white text-sm font-body font-light placeholder:text-white/40 focus:border-white/50 focus:outline-none transition-colors resize-none"
                    required
                    data-testid="contact-message-input"
                  />
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="bg-white text-black rounded-full px-6 py-3 text-sm font-medium font-body flex items-center justify-center gap-2 hover:bg-white/90 transition-colors disabled:opacity-60"
                    data-testid="contact-submit-btn"
                  >
                    {status === "sending" ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  {status === "error" && (
                    <p className="text-red-400 text-xs text-center font-body" data-testid="contact-error">
                      Something went wrong. Please try again.
                    </p>
                  )}
                </form>
              )}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
